import { useEffect, useRef } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import { ExternalTimeline } from '@/features/oi/ExternalTimeline';
import { SearchCrosshair } from '@/features/oi/SearchCrosshair';
import { useInfraSites } from '@/lib/api/queries';
import type { OiSummary } from '@/lib/api/types';
import {
  isUserPositioned,
  markFramed,
  markUserPositioned,
  shouldAutoFit,
} from '@/lib/map/cameraMemory';
import { visibleFleet } from '@/lib/map/droneFilter';
import { createMap, disposeMap, getMap } from '@/lib/map/instance';
import { subscribeGeofenceLayer } from '@/lib/map/layers/geofences';
import { subscribeInfraLayer } from '@/lib/map/layers/infra';
import { subscribeOiLayer } from '@/lib/map/layers/oi';
import { subscribeOiSearchAreaLayer } from '@/lib/map/layers/oiSearchArea';
import { subscribeProximityLayers } from '@/lib/map/layers/proximityMaps';
import { subscribeRingsLayer } from '@/lib/map/layers/rings';
import { subscribeSurveillanceLayers } from '@/lib/map/layers/surveillance';
import { subscribeDaaLines } from '@/lib/map/layers/daaLines';
import { subscribeFusedSourcesLayer } from '@/lib/map/layers/fusedSources';
import { subscribeRfLinksLayer } from '@/lib/map/layers/rfLinks';
import { subscribeTargetProjectionLayer } from '@/lib/map/layers/targetProjection';
import { subscribeTrailsLayer } from '@/lib/map/layers/trails';
import { subscribeWaypointsLayer } from '@/lib/map/layers/waypoints';
import { subscribeWeatherLayer } from '@/lib/map/layers/weather';
import { subscribeDroneMarkers } from '@/lib/map/markers/drones';
import { subscribePredMarkers } from '@/lib/map/markers/predProjection';
// Source-split rendering: MQTT-sourced aircraft use the legacy hand-drawn
// plane layer (with tooltip / ack / proximity colour intact); REST-sourced
// aircraft use the new tar1090 layer. Aircraft seen by both render once
// only — the OLD layer dedupes against useAircraftStore at sync time.
import { subscribeRfOperatorMarkers } from '@/lib/map/markers/rfOperators';
import { subscribeTargetMarkers } from '@/lib/map/markers/targets';
import { getCachedLocation, requestUserLocation } from '@/lib/map/userLocation';
import { config } from '@/lib/config';
import { queryClient } from '@/lib/query';
import { useDronesStore } from '@/lib/store/drones';
import { useUiStore } from '@/lib/store/ui';
import { Minus, Plus } from 'lucide-react';
import type { LngLatBoundsLike, Map } from 'mapbox-gl';
import { LayerPanel } from './LayerPanel';

/**
 * Map lifecycle:
 *   - Map instance created ONCE on mount; disposed only on unmount.
 *   - Drone & target markers are DOM-based (mapboxgl.Marker) wrapping the
 *     mockup's exact SVG/HTML — see lib/map/markers/*.
 *   - GL layers (rings, trails, target-projection, oi, infra) attach on
 *     `load` + `style.load` and re-attach after a basemap swap.
 *   - Per-context layer visibility is toggled via `setLayoutProperty`.
 *
 * `BodyGrid` mounts this exactly once and never moves it, so "unmount" means
 * the whole body going away — not a fullscreen toggle or a tab click. When an
 * overlay context covers the map, `covered` goes true and the map is hidden
 * with `visibility` rather than being torn down.
 */
export function MapCanvas({ covered = false }: { covered?: boolean }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const basemap = useUiStore((s) => s.basemap);
  const ctx = useUiStore((s) => s.activeContext);
  const { data: infraSites } = useInfraSites();

  // 1) Create map ONCE (mount → unmount).
  useEffect(() => {
    if (!containerRef.current) return;
    // Build with the persisted basemap (not the hardcoded default) so a
    // remount after a context switch restores the user's choice instead of
    // snapping back to Light. Read imperatively so basemap isn't a dep of this
    // mount-once effect (the swap effect below handles later changes).
    mapRef.current = createMap(containerRef.current, { style: useUiStore.getState().basemap });
    const coreUnsubs: Array<() => void> = [];

    const attachCore = () => {
      coreUnsubs.splice(0, coreUnsubs.length).forEach((u) => u());
      const m = mapRef.current!;
      coreUnsubs.push(subscribeDroneMarkers(m));
      coreUnsubs.push(subscribeTargetMarkers(m));
      coreUnsubs.push(subscribeRfOperatorMarkers(m));
      coreUnsubs.push(subscribePredMarkers(m));
      coreUnsubs.push(subscribeRingsLayer(m));
      // Geofences go in first so they sit under the OI volumes.
      coreUnsubs.push(subscribeGeofenceLayer(m));
      coreUnsubs.push(subscribeOiLayer(m));
      coreUnsubs.push(subscribeOiSearchAreaLayer(m));
      coreUnsubs.push(subscribeWaypointsLayer(m));
      coreUnsubs.push(subscribeTrailsLayer(m));
      coreUnsubs.push(subscribeTargetProjectionLayer(m));
      coreUnsubs.push(subscribeRfLinksLayer(m));
      coreUnsubs.push(subscribeFusedSourcesLayer(m));
      coreUnsubs.push(subscribeDaaLines(m));
      coreUnsubs.push(subscribeProximityLayers(m));
      coreUnsubs.push(subscribeSurveillanceLayers(m));
      coreUnsubs.push(subscribeWeatherLayer(m));
      // Infra reads sites from the TanStack Query cache, so it joins the
      // core attach list and re-attaches with the others on `style.load`
      // after a basemap swap.
      coreUnsubs.push(subscribeInfraLayer(m));
    };

    // Once the user positions the map, it's theirs — no context auto-frames
    // over it again. `originalEvent` is what separates a real drag/wheel/pinch
    // from our own programmatic fits, which fire the same events without one.
    const onUserGesture = (e: unknown) => {
      if (!(e as { originalEvent?: unknown } | undefined)?.originalEvent) return;
      markUserPositioned();
    };
    for (const ev of ['dragend', 'zoomend', 'rotateend', 'pitchend'] as const) {
      mapRef.current.on(ev, onUserGesture);
    }

    mapRef.current.once('load', attachCore);
    mapRef.current.on('style.load', attachCore);

    // ENG-277 — open at the viewer's location instead of the tenant default.
    // It replaces the startup zoom-to-fleet only: OI / infra / surv still
    // frame their own data on first visit. A known location means the map was
    // already built there, so drones must not fit away from it.
    if (getCachedLocation()) markFramed('drones');
    let disposed = false;
    void requestUserLocation().then((p) => {
      const m = mapRef.current;
      if (!p || disposed || !m) return;
      // Never pull the camera from under someone who has moved it, opened
      // another context, or picked a drone while the prompt was up.
      if (isUserPositioned()) return;
      if (useUiStore.getState().activeContext !== 'drones') return;
      if (useDronesStore.getState().selectedId) return;
      markFramed('drones');
      m.easeTo({ center: [p[1], p[0]], zoom: config.map.defaultZoom, duration: 800 });
    });

    // Pan to selected drone (mockup `selectDrone` → setView). Fleet view: fit
    // bounds to all drones (mockup `zoomToFleet`).
    const unsubSel = useDronesStore.subscribe(
      (s) => s.selectedId,
      (selId) => {
        const m = mapRef.current;
        if (!m) return;
        const state = useDronesStore.getState();
        if (selId) {
          const d = state.byId[selId];
          if (!d) return;
          // Mockup behaviour: keep the current zoom (fleet-level), just pan
          // the drone into view. Don't force-zoom to 13 — that hides peers.
          m.easeTo({ center: [d.telemetry.lng, d.telemetry.lat], duration: 500 });
        } else {
          const fleet = Object.values(state.byId);
          if (fleet.length === 0) return;
          let minLng = Number.POSITIVE_INFINITY,
            minLat = Number.POSITIVE_INFINITY,
            maxLng = Number.NEGATIVE_INFINITY,
            maxLat = Number.NEGATIVE_INFINITY;
          for (const d of fleet) {
            const { lng, lat } = d.telemetry;
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
          }
          m.fitBounds(
            [
              [minLng, minLat],
              [maxLng, maxLat],
            ],
            { padding: 80, maxZoom: 11, duration: 600 },
          );
        }
      },
    );

    return () => {
      disposed = true;
      coreUnsubs.forEach((u) => u());
      unsubSel();
      const m = mapRef.current;
      if (m) {
        for (const ev of ['dragend', 'zoomend', 'rotateend', 'pitchend'] as const) {
          m.off(ev, onUserGesture);
        }
      }
      disposeMap();
      mapRef.current = null;
    };
  }, []);

  // 1b) Re-assert size and force a frame when the map is revealed again.
  // Hiding with `visibility` keeps the container's box, so the size normally
  // hasn't changed — but a canvas that was hidden can have its frame dropped
  // by the compositor, so repaint rather than trust the stale one.
  useEffect(() => {
    if (covered) return;
    const m = mapRef.current;
    if (!m) return;
    m.resize();
    m.triggerRepaint();
  }, [covered]);

  // 2) Basemap swap. The ref starts at the mount-time basemap (which createMap
  // already loaded), so we only `setStyle` on an actual change — never
  // redundantly on mount, and never skipping a non-default persisted choice.
  const lastBasemapRef = useRef<string>(basemap);
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    if (lastBasemapRef.current === basemap) return;
    lastBasemapRef.current = basemap;
    m.setStyle(basemap);
  }, [basemap]);

  // 3) Per-context layer visibility (GL layers only; DOM markers handle themselves).
  //
  // `apply()` is idempotent — repeated calls with the same ctx are no-ops.
  // The challenge: `isStyleLoaded()` is briefly false right after a ctx click
  // (fitBounds / autoFit kicks off a camera move that marks the style "dirty"
  // for a frame). `'load'` and `'style.load'` only fire on initial style load
  // or basemap swap — they don't fire on camera-only changes. So we listen on
  // `'idle'`, which fires whenever the map settles after any change. Each
  // `apply()` call is just a few `setLayoutProperty` no-ops, so the overhead
  // of running it on every idle is negligible.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const apply = () => {
      if (!m.isStyleLoaded()) return;
      const setVis = (id: string, visible: boolean) => {
        if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
      };
      const inDrones = ctx === 'drones';
      const inOi = ctx === 'oi';
      const inInfra = ctx === 'infra';
      setVis('lyr-rings', inDrones);
      setVis('lyr-trails', inDrones);
      setVis('lyr-trails-dots', inDrones);
      setVis('lyr-target-proj', inDrones);
      setVis('lyr-oi-fill', inDrones || inOi);
      setVis('lyr-oi-line', inDrones || inOi);
      setVis('lyr-waypoint-path', inDrones || inOi);
      setVis('lyr-waypoint-actual', inDrones || inOi);
      setVis('lyr-waypoint-nodes', inDrones || inOi);
      // No 'lyr-waypoint-eta-label' toggle — that's a DOM marker now (see
      // waypoints.ts), self-managing via syncWaypointsLayer's own context
      // gating rather than a Mapbox layer's visibility property.
      // Mockup parity: infra is an Infra-ctx-only layer. The Drones / OI / Surv
      // contexts each wipe foreign markers on entry (mockup `selectContext`
      // L4956-4973 / L5046 / L5126); we mirror that by gating the layer.
      setVis('lyr-infra', inInfra);
    };
    apply();
    m.on('idle', apply);
    return () => {
      m.off('idle', apply);
    };
  }, [ctx, basemap, infraSites]);

  // 4) Per-context auto-fit on FIRST entry to a context (mockup parity).
  //
  //   drones ctx → fit to fleet bbox (mockup `zoomToFleet` L5029)
  //   oi ctx     → fit to union of OI polygon bboxes (mockup `zoomToVisible` L5077)
  //   infra ctx  → fit to infra site bbox (mockup L5927)
  //   surv ctx   → handled by `lib/map/layers/surveillance.ts` (don't double-fit)
  //   rtm / inv  → no map fit (full-area overlays)
  //
  // The data the fit reads — fleet telemetry, OI REST cache, infra REST — is
  // populated asynchronously by MQTT / TanStack Query, and the map style itself
  // may not be loaded yet at effect-mount. So we attempt the fit on every
  // relevant signal and short-circuit via a `done` flag once one fit lands.
  //
  // This fires on FIRST entry only. Re-entering a context restores the camera
  // saved by 3b instead, so clicking between tabs no longer throws the user
  // back to the fleet bbox at zoom 11.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    if (ctx === 'surv' || ctx === 'rtm' || ctx === 'inv' || ctx === 'apps') return;

    // One shared camera: a tab switch must never move it. We frame a context
    // only on its first-ever visit, and only while the user hasn't positioned
    // the map themselves.
    if (!shouldAutoFit(ctx)) return;

    let done = false;
    // The OI placeholder below is allowed to run once, but must not count as
    // framing the context — the real polygons still need their turn.
    let placeholderShown = false;
    const tryFit = () => {
      // Re-asked on every retry, not just at mount: while this fit waits for
      // data, the user may move the map or their location may land (ENG-277).
      if (done || !shouldAutoFit(ctx)) return;
      if (!m.isStyleLoaded()) return;
      let bounds: [[number, number], [number, number]] | null = null;
      let usedFallback = false;
      const update = (lng: number, lat: number) => {
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
        if (!bounds) {
          bounds = [
            [lng, lat],
            [lng, lat],
          ];
          return;
        }
        if (lng < bounds[0][0]) bounds[0][0] = lng;
        if (lng > bounds[1][0]) bounds[1][0] = lng;
        if (lat < bounds[0][1]) bounds[0][1] = lat;
        if (lat > bounds[1][1]) bounds[1][1] = lat;
      };

      if (ctx === 'drones') {
        const fleet = visibleFleet();
        for (const d of fleet) update(d.telemetry.lng, d.telemetry.lat);
      } else if (ctx === 'oi') {
        const oiList = queryClient.getQueryData<OiSummary[]>(['oi', 'list']) ?? [];
        for (const oi of oiList) {
          const ring = oi.polygon?.coordinates?.[0];
          if (!ring) continue;
          for (const [lng, lat] of ring) update(lng, lat);
        }
        // Empty OI cache fallback: fit to fleet so the user still sees a useful
        // view (the per-drone box fallback in `lib/map/layers/oi.ts` paints
        // boxes around drones until the real cache lands).
        if (!bounds) {
          const fleet = Object.values(useDronesStore.getState().byId);
          for (const d of fleet) update(d.telemetry.lng, d.telemetry.lat);
          usedFallback = true;
        }
      } else if (ctx === 'infra') {
        for (const s of infraSites ?? []) update(s.lng, s.lat);
      }

      if (!bounds) return; // no data yet — retry on next signal
      const b = bounds as [[number, number], [number, number]];
      if (usedFallback) {
        // Show the placeholder once, then keep listening for the real data.
        if (placeholderShown) return;
        placeholderShown = true;
      } else {
        done = true;
        markFramed(ctx);
      }
      const same = b[0][0] === b[1][0] && b[0][1] === b[1][1];
      if (same) {
        m.easeTo({ center: b[0], zoom: Math.max(m.getZoom(), 11), duration: 600 });
      } else {
        m.fitBounds(b as LngLatBoundsLike, { padding: 80, maxZoom: 11, duration: 600 });
      }
    };

    // Initial attempt (debounced so a rapid ctx-flip doesn't fight an ease).
    const initialId = window.setTimeout(tryFit, 80);
    // Retry on map lifecycle signals (style settled / first idle).
    m.on('load', tryFit);
    m.on('style.load', tryFit);
    // Retry when the underlying data lands.
    let unsubDrones: (() => void) | null = null;
    let unsubQuery: (() => void) | null = null;
    if (ctx === 'drones' || ctx === 'oi') {
      unsubDrones = useDronesStore.subscribe((s) => Object.keys(s.byId).length, tryFit);
    }
    if (ctx === 'oi') {
      unsubQuery = queryClient.getQueryCache().subscribe((event) => {
        if (event.query.queryKey[0] === 'oi') tryFit();
      });
    }

    return () => {
      done = true;
      clearTimeout(initialId);
      m.off('load', tryFit);
      m.off('style.load', tryFit);
      unsubDrones?.();
      unsubQuery?.();
    };
  }, [ctx, infraSites]);

  return (
    <div className="absolute inset-0">
      <div ref={containerRef} className="w-full h-full" />
      <SearchCrosshair />
      <LayerPanel />
      <RingZoomButtons />
      <ExternalTimeline />
      <div className="absolute bottom-[calc(0.75rem+var(--map-inset,0px))] right-3 flex flex-col gap-1 z-10">
        <button
          type="button"
          onClick={() => {
            markUserPositioned();
            getMap()?.zoomIn();
          }}
          className="size-8 rounded-md bg-(--color-surface) border border-(--color-border) shadow-(--shadow-card) flex items-center justify-center hover:bg-(--color-bg)"
          aria-label="Zoom in"
        >
          <Plus size={14} />
        </button>
        <button
          type="button"
          onClick={() => {
            markUserPositioned();
            getMap()?.zoomOut();
          }}
          className="size-8 rounded-md bg-(--color-surface) border border-(--color-border) shadow-(--shadow-card) flex items-center justify-center hover:bg-(--color-bg)"
          aria-label="Zoom out"
        >
          <Minus size={14} />
        </button>
      </div>
    </div>
  );
}

/**
 * Mockup `#ring-zoom-btns` — visible only when a drone is selected.
 * Each preset fits the map to roughly the diameter of one ring (NMAC / SWC / EVAL).
 */
function RingZoomButtons() {
  const sel = useDronesStore((s) => s.selectedId);
  const ctx = useUiStore((s) => s.activeContext);
  if (!sel || ctx !== 'drones') return null;
  const zoomTo = (kind: 'nmac' | 'swc' | 'eval') => {
    const m = getMap();
    if (!m) return;
    const d = useDronesStore.getState().byId[sel];
    if (!d) return;
    // Clicking a ring preset is the user choosing a frame, so it should
    // survive a tab switch like a drag would.
    markUserPositioned();
    const { lng, lat, nmacRadius, swcRadius, evalRadius } = d.telemetry;
    const r = kind === 'nmac' ? nmacRadius : kind === 'swc' ? swcRadius : evalRadius;
    // 1° lat ≈ 111_320 m. Use bbox = center +/- (r * 1.6) meters.
    const padded = r * 1.6;
    const dLat = padded / 111_320;
    const dLng = padded / (111_320 * Math.cos((lat * Math.PI) / 180));
    m.fitBounds(
      [
        [lng - dLng, lat - dLat],
        [lng + dLng, lat + dLat],
      ],
      { padding: 60, duration: 500 },
    );
  };
  return (
    <div className="absolute top-3 right-3 flex gap-1 z-10">
      {(['nmac', 'swc', 'eval'] as const).map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => zoomTo(k)}
          className="h-7 px-2 rounded-md bg-(--color-surface) border border-(--color-border) shadow-(--shadow-card) text-[0.692em] font-semibold uppercase tracking-wider text-(--color-text2) hover:border-(--color-green) hover:text-(--color-green)"
          aria-label={`Zoom to ${k}`}
        >
          {k}
        </button>
      ))}
    </div>
  );
}
